import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';

interface ContactStudentDialogProps {
  open: boolean;
  onClose: () => void;
  studentName: string;
  studentId: number;
}

const ContactStudentDialog: React.FC<ContactStudentDialogProps> = ({
  open,
  onClose,
  studentName,
  studentId: _studentId
}) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [template, setTemplate] = useState('custom');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const templates = {
    custom: { subject: '', message: '' },
    check_in: {
      subject: 'Checking In On Your Progress',
      message: `Hi ${studentName},\n\nI wanted to check in and see how you're doing with the recent assignments. I've noticed you might be facing some challenges, and I'm here to help.\n\nWould you like to schedule a time to discuss your progress? Please let me know what works best for you.\n\nBest regards`
    },
    encouragement: {
      subject: 'You Can Do This!',
      message: `Hi ${studentName},\n\nI wanted to reach out to let you know that I believe in you! Everyone learns at their own pace, and it's completely normal to find some topics challenging.\n\nRemember, making mistakes is part of learning. Keep working hard, and don't hesitate to ask for help when you need it.\n\nYou've got this!\n\nBest regards`
    },
    meeting_request: {
      subject: 'Meeting Request - Academic Support',
      message: `Hi ${studentName},\n\nI'd like to schedule a brief meeting with you to discuss your academic progress and see how I can better support your learning.\n\nPlease let me know your availability this week, and we can find a time that works for both of us.\n\nLooking forward to talking with you!\n\nBest regards`
    },
    practice_recommendation: {
      subject: 'Practice Recommendations',
      message: `Hi ${studentName},\n\nI've identified some areas where additional practice might help strengthen your understanding. I've prepared some resources and exercises that I think you'll find helpful.\n\nLet's work together to build your confidence in these topics. Please reach out if you have any questions about the materials.\n\nBest regards`
    }
  };

  const handleTemplateChange = (templateKey: string) => {
    setTemplate(templateKey);
    if (templateKey !== 'custom') {
      const selectedTemplate = templates[templateKey as keyof typeof templates];
      setSubject(selectedTemplate.subject);
      setMessage(selectedTemplate.message);
    } else {
      setSubject('');
      setMessage('');
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      setError('Please fill in both subject and message');
      return;
    }

    setSending(true);
    setError(null);

    try {
      // TODO: Implement actual API call to send message/email
      // await teacherService.contactStudent(studentId, { subject, message });

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setSubject('');
    setMessage('');
    setTemplate('custom');
    setSuccess(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EmailIcon />
          <Typography variant="h6">Contact {studentName}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Message sent successfully!
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Message Template</InputLabel>
            <Select
              value={template}
              onChange={(e) => handleTemplateChange(e.target.value)}
              label="Message Template"
            >
              <MenuItem value="custom">Custom Message</MenuItem>
              <MenuItem value="check_in">Check-In / Progress Update</MenuItem>
              <MenuItem value="encouragement">Encouragement</MenuItem>
              <MenuItem value="meeting_request">Meeting Request</MenuItem>
              <MenuItem value="practice_recommendation">Practice Recommendation</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            required
            disabled={sending}
          />

          <TextField
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            rows={8}
            fullWidth
            required
            disabled={sending}
            helperText="This message will be sent to the student's registered email address"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={sending}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          disabled={sending || !subject.trim() || !message.trim()}
          startIcon={sending ? <CircularProgress size={16} /> : <EmailIcon />}
        >
          {sending ? 'Sending...' : 'Send Message'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactStudentDialog;
